// this file defines both the backend api type (snake_case) and the frontend type (camelCase)
// always use IAiModel (camelCase) in the frontend, and convert from IAiModelApi at the api boundary
import { SATOSHIS } from '../lib/bsv-util'

export interface IContextualPricingRulesApi {
    formula: 'duration_plus_reference_video' | 'image_quality_resolution' | 'input_characters'
    default_resolution?: string | null
    resolution_cost_per_second_usd?: Record<string, number | string> | null
    reference_image_free_count?: number | null
    reference_image_cost_usd?: number | string | null
    reference_video_price_multiplier?: number | string | null
    default_quality?: string | null
    resolution_quality_cost_usd?: Record<string, Record<string, number | string>> | null
    default_output_image_count?: number | null
    cost_per_1000_characters_usd?: number | string | null
    minimum_units?: number | string | null
}

export type ContextualPricingRules =
    | {
          formula: 'duration_plus_reference_video'
          defaultResolution: string
          resolutionCostPerSecondUsd: Record<string, number>
          referenceImageFreeCount: number
          referenceImageCostUsd: number
          referenceVideoPriceMultiplier: number
      }
    | {
          formula: 'image_quality_resolution'
          defaultResolution: string
          defaultQuality: string
          resolutionQualityCostUsd: Record<string, Record<string, number>>
          referenceImageCostUsd: number
          defaultOutputImageCount: number
      }
    | {
          formula: 'input_characters'
          costPer1000CharactersUsd: number
          minimumUnits: number
      }

export interface AiModelPricingContext {
    resolution?: string | null
    quality?: string | null
    referenceImageCount?: number | null
    referenceVideoCount?: number | null
    outputImageCount?: number | null
    referenceVideoDurationSeconds?: number | null
    textCharacterCount?: number | null
}

// snake_case type for backend api
export interface IAiModelApi {
    supported_inputs?: string[]
    required_inputs?: string[]
    id: string
    name: string
    human_name: string
    display_name?: string | null
    description: string
    usd_cost: number
    model_type: 'image' | 'video' | 'text' | 'audio'
    usd_treechat_private_price: number
    provider: string
    created_at?: string
    updated_at?: string
    action_tag_name: string
    usd_treechat_public_price: number
    can_remix_video?: boolean
    can_remix_image?: boolean
    can_remix_audio?: boolean
    reference_image_limit?: number | null
    reference_video_limit?: number | null
    reference_audio_limit?: number | null
    sort_priority?: number
    // Duration constraint fields for per-second pricing
    allowed_durations?: number[] | null
    allowed_resolutions?: string[] | null
    min_duration_seconds?: number | null
    max_duration_seconds?: number | null
    default_duration_seconds?: number | null
    // Per-second pricing fields
    usd_cost_per_second?: number | null
    usd_treechat_private_price_per_second?: number | null
    usd_treechat_public_price_per_second?: number | null
    estimated_provider_cost_usd?: number | null
    estimated_provider_cost_per_second_usd?: number | null
    pricing_margin_bps?: number | null
    pricing_estimator_version?: string | null
    pricing_tier_key?: string | null
    contextual_pricing_rules?: IContextualPricingRulesApi | null
    description_short?: string
    openclaw_integration_id?: string | null
    openclaw_owner_user_id?: string | null
    openclaw_owner_username?: string | null
    openclaw_alias?: string | null
    openclaw_visibility_scope?: 'owner' | 'stream' | null
    openclaw_team_ids?: string[] | null
}

// camelCase type for frontend
export interface IAiModel {
    supportedInputs?: string[]
    requiredInputs?: string[]
    id: string
    name: string
    displayName?: string | null
    usdCost: number
    modelType: 'image' | 'video' | 'text' | 'audio'
    usdTreechatPrivatePrice: number
    provider: string
    createdAt?: string
    updatedAt?: string
    actionTagName: string
    usdTreechatPublicPrice: number
    humanName: string
    description: string
    descriptionShort?: string
    canRemixVideo?: boolean
    canRemixImage?: boolean
    canRemixAudio?: boolean
    referenceImageLimit?: number | null
    referenceVideoLimit?: number | null
    referenceAudioLimit?: number | null
    sortPriority?: number
    // Duration constraint fields for per-second pricing
    allowedDurations?: number[] | null
    minDurationSeconds?: number | null
    maxDurationSeconds?: number | null
    defaultDurationSeconds?: number | null
    allowedResolutions?: string[] | null
    // Per-second pricing fields
    usdCostPerSecond?: number | null
    usdTreechatPrivatePricePerSecond?: number | null
    usdTreechatPublicPricePerSecond?: number | null
    estimatedProviderCostUsd?: number | null
    estimatedProviderCostPerSecondUsd?: number | null
    pricingMarginBps?: number | null
    pricingEstimatorVersion?: string | null
    pricingTierKey?: string | null
    contextualPricingRules?: ContextualPricingRules | null
    openclawIntegrationId?: string | null
    openclawOwnerUserId?: string | null
    openclawOwnerUsername?: string | null
    openclawAlias?: string | null
    openclawVisibilityScope?: 'owner' | 'stream' | null
    openclawTeamIds?: string[] | null
}

export const ACTION_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
    grok_imagine: '!grok',
    grokimage: '!grok',
    grokvideo: '!grok',
    'grokvideo1.5': '!grok1.5',
    kling3_omni: 'Kling Omni',
}

export const KLING_OMNI_ACTION_TAG = 'kling3_omni'
const KLING_OMNI_VISIBLE_ACTION_TAGS = new Set(['kling3'])
const DEFAULT_PRICING_MARGIN_BPS = 1500

function normalizedActionTagKey(actionTag: string | null | undefined): string {
    return actionTag?.toString().trim().replace(/^!+/, '').toLowerCase() || ''
}

// hardcoded mapping from api to camelcase
export function apiModelToCamel(api: IAiModelApi): IAiModel {
    return {
        id: api.id,
        supportedInputs: api.supported_inputs,
        requiredInputs: api.required_inputs,
        name: api.name,
        displayName:
            api.display_name ||
            ACTION_DISPLAY_NAME_OVERRIDES[normalizedActionTagKey(api.action_tag_name)] ||
            null,
        usdCost: api.usd_cost,
        modelType: api.model_type,
        usdTreechatPrivatePrice: api.usd_treechat_private_price,
        provider: api.provider,
        createdAt: api.created_at,
        updatedAt: api.updated_at,
        actionTagName: api.action_tag_name,
        usdTreechatPublicPrice: api.usd_treechat_public_price,
        humanName: api.human_name,
        description: api.description,
        descriptionShort: api.description_short,
        canRemixVideo: api.can_remix_video,
        canRemixImage: api.can_remix_image,
        canRemixAudio: api.can_remix_audio,
        referenceImageLimit: api.reference_image_limit,
        referenceVideoLimit: api.reference_video_limit,
        referenceAudioLimit: api.reference_audio_limit,
        sortPriority: api.sort_priority,
        // Duration constraint fields
        allowedDurations: api.allowed_durations,
        minDurationSeconds: api.min_duration_seconds,
        maxDurationSeconds: api.max_duration_seconds,
        defaultDurationSeconds: api.default_duration_seconds,
        allowedResolutions: api.allowed_resolutions,
        // Per-second pricing fields
        usdCostPerSecond: api.usd_cost_per_second,
        usdTreechatPrivatePricePerSecond: api.usd_treechat_private_price_per_second,
        usdTreechatPublicPricePerSecond: api.usd_treechat_public_price_per_second,
        estimatedProviderCostUsd: api.estimated_provider_cost_usd,
        estimatedProviderCostPerSecondUsd: api.estimated_provider_cost_per_second_usd,
        pricingMarginBps: api.pricing_margin_bps,
        pricingEstimatorVersion: api.pricing_estimator_version,
        pricingTierKey: api.pricing_tier_key,
        contextualPricingRules: contextualPricingRulesToCamel(api.contextual_pricing_rules),
        openclawIntegrationId: api.openclaw_integration_id,
        openclawOwnerUserId: api.openclaw_owner_user_id,
        openclawOwnerUsername: api.openclaw_owner_username,
        openclawAlias: api.openclaw_alias,
        openclawVisibilityScope: api.openclaw_visibility_scope,
        openclawTeamIds: api.openclaw_team_ids,
    }
}

function contextualPricingRulesToCamel(
    rules: IContextualPricingRulesApi | null | undefined,
): ContextualPricingRules | null {
    if (rules?.formula === 'duration_plus_reference_video') {
        const resolutionCostPerSecondUsd = Object.fromEntries(
            Object.entries(rules.resolution_cost_per_second_usd || {}).map(([resolution, cost]) => [
                resolution.toUpperCase(),
                numberValue(cost) ?? 0,
            ]),
        )
        return {
            formula: rules.formula,
            defaultResolution: rules.default_resolution?.toUpperCase() || '768P',
            resolutionCostPerSecondUsd,
            referenceImageFreeCount: numberValue(rules.reference_image_free_count) ?? 0,
            referenceImageCostUsd: numberValue(rules.reference_image_cost_usd) ?? 0,
            referenceVideoPriceMultiplier: numberValue(rules.reference_video_price_multiplier) ?? 1,
        }
    }

    if (rules?.formula === 'input_characters') {
        return {
            formula: rules.formula,
            costPer1000CharactersUsd: numberValue(rules.cost_per_1000_characters_usd) ?? 0,
            minimumUnits: numberValue(rules.minimum_units) ?? 0,
        }
    }

    if (rules?.formula === 'image_quality_resolution') {
        const resolutionQualityCostUsd = Object.fromEntries(
            Object.entries(rules.resolution_quality_cost_usd || {}).map(
                ([resolution, qualityCosts]) => [
                    resolution.toLowerCase(),
                    Object.fromEntries(
                        Object.entries(qualityCosts || {}).map(([quality, cost]) => [
                            quality.toLowerCase(),
                            numberValue(cost) ?? 0,
                        ]),
                    ),
                ],
            ),
        )
        return {
            formula: rules.formula,
            defaultResolution: rules.default_resolution?.toLowerCase() || '1k',
            defaultQuality: rules.default_quality?.toLowerCase() || 'medium',
            resolutionQualityCostUsd,
            referenceImageCostUsd: numberValue(rules.reference_image_cost_usd) ?? 0,
            defaultOutputImageCount: numberValue(rules.default_output_image_count) ?? 1,
        }
    }

    return null
}

// type for ui defaults for ai models (camelCase)
export type AiModelUIDefault = {
    actionTagName: string
    humanName: string
    displayName?: string | null
    description: string
}

export function normalizeActionTag(actionTag: string | null | undefined): string {
    return normalizedActionTagKey(actionTag)
}

export function actionDisplayNameForModel(
    model: Partial<Pick<IAiModel, 'actionTagName' | 'displayName'>> | null | undefined,
    fallbackTag?: string | null,
): string | null {
    const tag = normalizeActionTag(fallbackTag || model?.actionTagName)
    return model?.displayName || ACTION_DISPLAY_NAME_OVERRIDES[tag] || null
}

export const LEGACY_ACTION_TAG_ALIASES: Record<string, string> = {
    stable_audio: 'stableaudio',
    eleven: 'eleven_tts',
    elevenlabs: 'eleven_tts',
    '11': 'eleven_tts',
    eleven_music: 'elevenmusic',
    '11music': 'elevenmusic',
    mmaudio: 'video_sfx',
    foley: 'video_sfx',
    sfx: 'video_sfx',
}

export function canonicalActionTagFor(actionTag: string | null | undefined): string {
    const normalizedActionTag = normalizeActionTag(actionTag)
    const legacyAlias = LEGACY_ACTION_TAG_ALIASES[normalizedActionTag]
    return legacyAlias || normalizedActionTag
}

export function isKlingOmniActionTag(actionTag: string | null | undefined): boolean {
    return normalizeActionTag(actionTag) === KLING_OMNI_ACTION_TAG
}

export function isKlingOmniVisibleActionTag(actionTag: string | null | undefined): boolean {
    const normalizedActionTag = normalizeActionTag(actionTag)
    const canonicalActionTag = canonicalActionTagFor(normalizedActionTag)
    return (
        KLING_OMNI_VISIBLE_ACTION_TAGS.has(normalizedActionTag) ||
        KLING_OMNI_VISIBLE_ACTION_TAGS.has(canonicalActionTag)
    )
}

function collectionHasItems(value: unknown): boolean {
    return Array.isArray(value) && value.length > 0
}

export function actionHasKlingOmniReferences(action: any): boolean {
    if (!action || typeof action !== 'object') return false

    return (
        collectionHasItems(action.referenceImageInputs) ||
        collectionHasItems(action.referenceVideoInputs) ||
        collectionHasItems(action.inputs?.references?.images) ||
        collectionHasItems(action.inputs?.references?.videos)
    )
}

export function shouldUseKlingOmniModel(
    actionTag: string | null | undefined,
    action: any,
): boolean {
    return isKlingOmniVisibleActionTag(actionTag) && actionHasKlingOmniReferences(action)
}

export function findTransparentModelForAction(
    aiModels: IAiModel[],
    actionTag: string | null | undefined,
    action: any,
): IAiModel | undefined {
    if (!shouldUseKlingOmniModel(actionTag, action)) return undefined
    return findModelByActionTag(aiModels, KLING_OMNI_ACTION_TAG)
}

export function findEffectiveModelForAction(
    aiModels: IAiModel[],
    actionTag: string | null | undefined,
    action: any,
): IAiModel | undefined {
    const actionModelId = action?.ai_model_id || action?.aiModelId || null
    const explicitModel = actionModelId
        ? aiModels.find(model => model?.id === actionModelId)
        : undefined
    const transparentModel = findTransparentModelForAction(aiModels, actionTag, action)

    return transparentModel || explicitModel || findModelByActionTag(aiModels, actionTag || '')
}

export function findModelByActionTag(
    aiModels: IAiModel[],
    actionTag: string,
): IAiModel | undefined {
    const normalizedActionTag = normalizeActionTag(actionTag)
    const canonicalActionTag = canonicalActionTagFor(normalizedActionTag)
    return aiModels.find(model => {
        const modelActionTag = normalizeActionTag(model.actionTagName)
        return (
            modelActionTag === canonicalActionTag ||
            modelActionTag === normalizedActionTag ||
            (canonicalActionTag === 'flux' && modelActionTag === 'flux2')
        )
    })
}

export function getPublicModelCost(model: IAiModel | undefined): number {
    return getPublicModelCostWithDuration(model, null)
}

export function getPrivateModelCost(model: IAiModel | undefined): number {
    return getPrivateModelCostWithDuration(model, null)
}

export function getEffectiveDurationSeconds(
    model: IAiModel | undefined,
    durationSeconds: number | null,
): number | null {
    return durationSeconds ?? model?.defaultDurationSeconds ?? null
}

function numberValue(value: number | string | null | undefined): number | null {
    const number = Number(value)
    return Number.isFinite(number) ? number : null
}

function ceilCents(value: number): number {
    return Math.ceil(value * 100) / 100
}

function configuredModelCostWithDuration(
    model: IAiModel | undefined,
    durationSeconds: number | null,
): number {
    const effectiveDurationSeconds = getEffectiveDurationSeconds(model, durationSeconds)
    const pricePerSecond = numberValue(model?.usdTreechatPrivatePricePerSecond)
    if (pricePerSecond != null && pricePerSecond > 0 && effectiveDurationSeconds) {
        return pricePerSecond * effectiveDurationSeconds
    }

    return numberValue(model?.usdTreechatPrivatePrice) ?? 0.1
}

function estimatedProviderCostWithDuration(
    model: IAiModel | undefined,
    durationSeconds: number | null,
    pricingContext?: AiModelPricingContext | null,
): number | null {
    const contextualProviderCost = estimatedContextualProviderCost(
        model,
        durationSeconds,
        pricingContext,
    )
    if (contextualProviderCost != null) return contextualProviderCost

    const effectiveDurationSeconds = getEffectiveDurationSeconds(model, durationSeconds)
    const providerCostPerSecond = numberValue(model?.estimatedProviderCostPerSecondUsd)
    if (providerCostPerSecond != null && providerCostPerSecond > 0 && effectiveDurationSeconds) {
        return providerCostPerSecond * effectiveDurationSeconds
    }

    return numberValue(model?.estimatedProviderCostUsd)
}

function estimatedContextualProviderCost(
    model: IAiModel | undefined,
    durationSeconds: number | null,
    pricingContext?: AiModelPricingContext | null,
): number | null {
    const rules = model?.contextualPricingRules
    if (!rules || !pricingContext) return null

    if (rules.formula === 'duration_plus_reference_video') {
        const outputSeconds = getEffectiveDurationSeconds(model, durationSeconds)
        if (!outputSeconds || outputSeconds <= 0) return null

        const resolution =
            pricingContext.resolution?.toString().toUpperCase() || rules.defaultResolution
        const unitCost =
            numberValue(rules.resolutionCostPerSecondUsd[resolution]) ??
            numberValue(rules.resolutionCostPerSecondUsd[rules.defaultResolution])
        if (unitCost == null || unitCost <= 0) return null

        const referenceVideoSeconds = Math.max(
            numberValue(pricingContext.referenceVideoDurationSeconds) ?? 0,
            0,
        )
        const hasVideoReferences =
            Math.max(numberValue(pricingContext.referenceVideoCount) ?? 0, 0) > 0 ||
            referenceVideoSeconds > 0
        const videoPriceMultiplier = hasVideoReferences ? rules.referenceVideoPriceMultiplier : 1
        const referenceImageCount = Math.max(
            Math.floor(numberValue(pricingContext.referenceImageCount) ?? 0),
            0,
        )
        const paidReferenceImageCount = Math.max(
            referenceImageCount - rules.referenceImageFreeCount,
            0,
        )

        return (
            unitCost * (outputSeconds + referenceVideoSeconds) * videoPriceMultiplier +
            rules.referenceImageCostUsd * paidReferenceImageCount
        )
    }

    if (rules.formula === 'image_quality_resolution') {
        const resolution =
            pricingContext.resolution?.toString().toLowerCase() || rules.defaultResolution
        const quality = pricingContext.quality?.toString().toLowerCase() || rules.defaultQuality
        const costs =
            rules.resolutionQualityCostUsd[resolution] ||
            rules.resolutionQualityCostUsd[rules.defaultResolution]
        const outputUnitCost = numberValue(costs?.[quality] ?? costs?.[rules.defaultQuality])
        if (outputUnitCost == null || outputUnitCost <= 0) return null

        const outputImageCount = Math.max(
            Math.floor(
                numberValue(pricingContext.outputImageCount) ?? rules.defaultOutputImageCount,
            ),
            1,
        )
        const referenceImageCount = Math.max(
            Math.floor(numberValue(pricingContext.referenceImageCount) ?? 0),
            0,
        )
        return outputUnitCost * outputImageCount + rules.referenceImageCostUsd * referenceImageCount
    }

    const characterCount = numberValue(pricingContext.textCharacterCount)
    if (characterCount == null || characterCount <= 0) return null

    const units = Math.max(characterCount / 1000, rules.minimumUnits)
    return rules.costPer1000CharactersUsd * units
}

function effectiveModelCostWithDuration(
    model: IAiModel | undefined,
    durationSeconds: number | null,
    pricingContext?: AiModelPricingContext | null,
): number {
    const configuredCost = configuredModelCostWithDuration(model, durationSeconds)

    const providerCost = estimatedProviderCostWithDuration(model, durationSeconds, pricingContext)
    if (providerCost == null || providerCost <= 0) return configuredCost

    const marginBps = numberValue(model?.pricingMarginBps) ?? DEFAULT_PRICING_MARGIN_BPS
    const minimumProfitableCost = ceilCents(providerCost * (1 + marginBps / 10000))
    return Math.max(configuredCost, minimumProfitableCost)
}

/**
 * Get public model cost considering duration for per-second pricing.
 * Falls back to the model default duration when the prompt omits one.
 */
export function getPublicModelCostWithDuration(
    model: IAiModel | undefined,
    durationSeconds: number | null,
    pricingContext?: AiModelPricingContext | null,
): number {
    return effectiveModelCostWithDuration(model, durationSeconds, pricingContext)
}

/**
 * Get private model cost considering duration for per-second pricing.
 * Falls back to the model default duration when the prompt omits one.
 */
export function getPrivateModelCostWithDuration(
    model: IAiModel | undefined,
    durationSeconds: number | null,
    pricingContext?: AiModelPricingContext | null,
): number {
    return effectiveModelCostWithDuration(model, durationSeconds, pricingContext)
}

function arrayValue(value: unknown): any[] {
    return Array.isArray(value) ? value : []
}

function firstNonEmptyArray(...values: unknown[]): any[] {
    return values.map(arrayValue).find(value => value.length > 0) || []
}

function mediaDurationSeconds(media: any): number | null {
    const duration = numberValue(
        media?.durationSeconds ?? media?.duration_seconds ?? media?.duration,
    )
    return duration != null && duration > 0 ? duration : null
}

export function getActionPricingContext(
    model: IAiModel | undefined,
    action: any,
): AiModelPricingContext {
    const settings = action?.settings || {}
    const inputs = action?.inputs || {}
    const references = inputs.references || {}
    const startImage = inputs.start_image || inputs.startImage
    const endImage = inputs.end_image || inputs.endImage
    const explicitKeyframeImageCount = Number(Boolean(startImage)) + Number(Boolean(endImage))
    const hasDirectImage = Boolean(
        action?.imageInput?.url ||
            action?.imageInputUrl ||
            action?.imageInput?.signedId ||
            action?.imageInput?.signed_id ||
            action?.imageInput?.attachmentId ||
            action?.imageInput?.attachment_id ||
            action?.imageInput?.blobId ||
            action?.imageInput?.blob_id ||
            action?.imageInput?.uploadId ||
            action?.imageInput?.upload_id ||
            inputs.image?.url ||
            inputs.image?.signed_id ||
            inputs.image?.signedId ||
            inputs.image?.attachment_id ||
            inputs.image?.attachmentId ||
            inputs.image?.blob_id ||
            inputs.image?.blobId ||
            inputs.image?.upload_id ||
            inputs.image?.uploadId ||
            startImage?.url ||
            startImage?.signed_id ||
            startImage?.signedId ||
            startImage?.attachment_id ||
            startImage?.attachmentId ||
            startImage?.blob_id ||
            startImage?.blobId ||
            startImage?.upload_id ||
            startImage?.uploadId,
    )
    const h3FirstFrameOverridesReferences =
        canonicalActionTagFor(model?.actionTagName) === 'h3' && hasDirectImage
    const referenceImages = h3FirstFrameOverridesReferences
        ? []
        : firstNonEmptyArray(action?.referenceImageInputs, references.images)
    const referenceVideos = h3FirstFrameOverridesReferences
        ? []
        : firstNonEmptyArray(action?.referenceVideoInputs, references.videos)
    const referenceVideoDurationSeconds = referenceVideos.reduce(
        (total, video) => total + (mediaDurationSeconds(video) ?? 0),
        0,
    )
    const text =
        action?.textInputValue ??
        action?.promptContent ??
        inputs.text?.value ??
        action?.prompt ??
        ''

    const directImageCount =
        model?.contextualPricingRules?.formula === 'image_quality_resolution' && hasDirectImage
            ? 1
            : 0

    return {
        resolution: action?.resolution ?? settings.resolution ?? null,
        quality: action?.quality ?? settings.quality ?? null,
        referenceImageCount: referenceImages.length + directImageCount + explicitKeyframeImageCount,
        referenceVideoCount: referenceVideos.length,
        outputImageCount: 1,
        referenceVideoDurationSeconds,
        textCharacterCount: typeof text === 'string' ? text.length : 0,
    }
}

export function usdToSats(usdAmount: number, exchangeRate: number | null): number | null {
    if (!exchangeRate) return null
    return Math.round((usdAmount / exchangeRate) * SATOSHIS)
}
